import React, { useState, useEffect, useMemo, useRef } from 'react';
import '../QuoteCalculator.css';

// Service area FSAs
const servicedFsaSet = new Set([
  // City of Toronto
  'M1B', 'M1C', 'M1E', 'M1G', 'M1H', 'M1J', 'M1K', 'M1L', 'M1M', 'M1N',
  'M1P', 'M1R', 'M1S', 'M1T', 'M1V', 'M1W', 'M1X', 'M2H', 'M2J', 'M2K',
  'M2L', 'M2M', 'M2N', 'M2P', 'M2R', 'M3A', 'M3B', 'M3C', 'M3H', 'M3J',
  'M3K', 'M3L', 'M3M', 'M3N', 'M4A', 'M4B', 'M4C', 'M4E', 'M4G', 'M4H',
  'M4J', 'M4K', 'M4L', 'M4M', 'M4N', 'M4P', 'M4R', 'M4S', 'M4T', 'M4V',
  'M4W', 'M4X', 'M4Y', 'M5A', 'M5B', 'M5C', 'M5E', 'M5G', 'M5H', 'M5J',
  'M5K', 'M5L', 'M5M', 'M5N', 'M5P', 'M5R', 'M5S', 'M5T', 'M5V', 'M5W',
  'M5X', 'M6A', 'M6B', 'M6C', 'M6E', 'M6G', 'M6H', 'M6J', 'M6K', 'M6L',
  'M6M', 'M6N', 'M6P', 'M6R', 'M6S', 'M7A', 'M7Y', 'M8V', 'M8W', 'M8X',
  'M8Y', 'M8Z', 'M9A', 'M9B', 'M9C', 'M9L', 'M9M', 'M9N', 'M9P', 'M9R',
  'M9V', 'M9W',
  // Mississauga
  'L4T', 'L4V', 'L4W', 'L4X', 'L4Y', 'L4Z', 'L5A', 'L5B', 'L5C', 'L5E',
  'L5G', 'L5H', 'L5J', 'L5K', 'L5L', 'L5M', 'L5N', 'L5P', 'L5R', 'L5S',
  'L5T', 'L5V', 'L5W',
  // Vaughan
  'L0J', 'L4H', 'L4J', 'L4K', 'L4L', 'L6A',
  // Markham
  'L3P', 'L3R', 'L3S', 'L6B', 'L6C', 'L6E', 'L6G'
]);

const ROOM_PRICES = {
  office: 131.99,
  studio: 142.99,
  one: 184.79,
  two: 205.9,
  three: 263.99,
  four: 305.79,
  five: 348.7,
  six: 388.3
};

const BATHROOM_PRICES = {
  one: 0,
  two: 40.91,
  three: 81.82,
  four: 122.73,
  five: 163.64,
  six: 204.55
};

const CLEANING_MULTIPLIERS = {
  basic: 1,
  moveInOut: 1.25,
  deep: 1.5
};

const EXTRA_PRICES = {
  fridge: 35.2,
  oven: 36.3,
  laundry: 27.5,
  windows: 88.0,
  blinds: 88.0,
};

const TOOLTIPS = {
  basic: '🧼 Keep your home fresh and shining with our Basic Cleaning — perfect for busy schedules, families, and weekly or bi-weekly care!',
  moveInOut: '🚚 Make your move stress-free with our detailed Move-In/Move-Out Cleaning — leave your old place spotless or start fresh in your new home!',
  deep: '🧽 Refresh your space from top to bottom with our Deep Cleaning — the perfect way to make your home feel brand new again!'
};

export default function QuoteCalculator() {
  const [step, setStep] = useState(0);
  const [postalCode, setPostalCode] = useState('');
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedBathroom, setSelectedBathroom] = useState(null);
  const [squareFeet, setSquareFeet] = useState('');
  const [selectedCleaning, setSelectedCleaning] = useState(null);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [runningTotal, setRunningTotal] = useState(0);
  const [error, setError] = useState('');
  const [tooltip, setTooltip] = useState('');
  const [inputValue, setInputValue] = useState('');
  // Email quote state
  const [email, setEmail] = useState('');
  const [sendState, setSendState] = useState('idle'); // idle | sending | success | error
  const [sendError, setSendError] = useState('');
  
  // Rate limiting state
  const cooldownMs = 5 * 60 * 1000; // 5 minutes
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [nowTick, setNowTick] = useState(Date.now());
  const sendingRef = useRef(false);
  
  // Restore cooldown from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('amf_quote_email_cooldown');
      if (stored) {
        const until = parseInt(stored, 10);
        if (!isNaN(until) && until > Date.now()) {
          setCooldownUntil(until);
        }
      }
    } catch {}
  }, []);
  
  // Ticker to update countdown display
  useEffect(() => {
    const id = setInterval(() => {
      setNowTick(Date.now());
    }, 1000);
    return () => clearInterval(id);
  }, []);
  
  // Calculate remaining cooldown time
  const remainingMs = useMemo(() => {
    return Math.max(0, cooldownUntil - nowTick);
  }, [cooldownUntil, nowTick]);
  
  // Format remaining time as M:SS
  const remainingText = useMemo(() => {
    if (remainingMs <= 0) return '';
    const totalSeconds = Math.ceil(remainingMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [remainingMs]);

  const isValidCanadianPostalCode = (code) => {
    const regex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
    return regex.test(code);
  };

  const isServicedPostalCode = (code) => {
    const fsa = code.substring(0, 3).toUpperCase();
    return servicedFsaSet.has(fsa);
  };

  const isValidSquareFeet = (sqft) => {
    return !isNaN(parseFloat(sqft)) && isFinite(sqft) && Number(sqft) > 0;
  };

  const getPricePerSquareFoot = (feet) => {
    if (feet <= 750) return 0;
    return (feet - 750) * 0.05;
  };

  const handleNext = () => {
    setError('');

    switch (step) {
      case 0: // Location
        if (!isValidCanadianPostalCode(inputValue)) {
          setError('Please enter a valid Canadian postal code (e.g., M5V 3A8).');
          return;
        }
        if (!isServicedPostalCode(inputValue)) {
          setError('Sorry but we currently do not service your area 🙁.');
          return;
        }
        setPostalCode(inputValue);
        setInputValue('');
        setStep(1);
        break;

      case 1: // Bedrooms
        if (!selectedRoom) return;
        setRunningTotal((prev) => prev + ROOM_PRICES[selectedRoom]);
        setStep(2);
        break;

      case 2: // Bathrooms
        if (!selectedBathroom) return;
        setRunningTotal((prev) => prev + BATHROOM_PRICES[selectedBathroom]);
        setStep(3);
        break;

      case 3: // Square Feet
        if (!isValidSquareFeet(inputValue)) {
          setError('Please enter a valid number for square feet.');
          return;
        }
        setSquareFeet(inputValue);
        setRunningTotal((prev) => prev + getPricePerSquareFoot(Number(inputValue)));
        setInputValue('');
        setStep(4);
        break;

      case 4: // Cleaning Type
        if (!selectedCleaning) return;
        setRunningTotal((prev) => prev * CLEANING_MULTIPLIERS[selectedCleaning]);
        setStep(5);
        break;

      case 5: // Extras
        const extrasTotal = selectedExtras.reduce((sum, extra) => sum + EXTRA_PRICES[extra], 0);
        setRunningTotal((prev) => prev + extrasTotal);
        setInputValue('Quote Completed!');
        setStep(6);
        break;

      case 6: // Book
        if (typeof window.gtag_report_conversion === 'function') {
          window.gtag_report_conversion();
        }
        window.open('https://app.zenmaid.com/book/a-m-facilities', '_blank');
        break;

      default:
        break;
    }
  };

  const toggleExtra = (extra) => {
    setSelectedExtras((prev) => (prev.includes(extra) ? prev.filter((e) => e !== extra) : [...prev, extra]));
  };

  const getStepLabel = () => {
    const labels = ['Location', 'Bedrooms', 'Bathrooms', 'Feet', 'Type', 'Extra'];
    return labels[step] || 'Extra';
  };

  const getRoomLabel = (room) => {
    const displayText = {
      office: 'Office',
      studio: 'Studio',
      one: '1 Bedroom',
      two: '2 Bedrooms',
      three: '3 Bedrooms',
      four: '4 Bedrooms',
      five: '5 Bedrooms',
      six: '6 Bedrooms',
    };
    return displayText[room] || room;
  };

  const getBathroomLabel = (bath) => {
    const map = { one: '1', two: '2', three: '3', four: '4', five: '5', six: '6' };
    return (map[bath] || bath) + ' Bathroom' + ((map[bath] || bath) === '1' ? '' : 's');
  };

  const getCleaningLabel = (type) => {
    if (!type) return '';
    return type === 'moveInOut' ? 'Move In/Out' : type.charAt(0).toUpperCase() + type.slice(1);
  };

  const buildQuoteSummaryText = () => {
    const extras = selectedExtras.length ? selectedExtras.join(', ') : 'None';
    return [
      'Thank you for using AM Facilities! Here is your quote summary:',
      '',
      `Postal Code: ${postalCode || '—'}`,
      `Bedrooms: ${selectedRoom ? getRoomLabel(selectedRoom) : '—'}`,
      `Bathrooms: ${selectedBathroom ? getBathroomLabel(selectedBathroom) : '—'}`,
      `Square Feet: ${squareFeet || '—'}`,
      `Cleaning Type: ${getCleaningLabel(selectedCleaning) || '—'}`,
      `Extras: ${extras}`,
      '',
      `Estimated Total: $${runningTotal.toFixed(2)}`,
      '',
      'We appreciate your interest. If you have any questions or would like to proceed, just reply to this email or book online: https://app.zenmaid.com/book/a-m-facilities',
    ].join('\n');
  };

  const buildQuoteSummaryHtml = () => {
    const extras = selectedExtras.length ? selectedExtras.join(', ') : 'None';
    const rows = [
      ['Postal Code', postalCode || '—'],
      ['Bedrooms', selectedRoom ? getRoomLabel(selectedRoom) : '—'],
      ['Bathrooms', selectedBathroom ? getBathroomLabel(selectedBathroom) : '—'],
      ['Square Feet', squareFeet || '—'],
      ['Cleaning Type', getCleaningLabel(selectedCleaning) || '—'],
      ['Extras', extras],
    ];
  // Brand palette (matches site theme)
  const brand = '#0e4a52'; // deep teal
  const accent = '#3B9846'; // brand green
  const textPrimary = '#23343b';
  const textMuted = '#607d8b';
  const surface = '#ffffff';
  const tableBorder = '#e7eef0';
  // Use a publicly accessible logo URL for email clients (env-configurable)
  const logoUrl = process.env.REACT_APP_LOGO_URL || 'https://amfacilities.ca/assets/logo.png';

    const rowsHtml = rows
      .map(
        ([k, v]) => `
        <tr>
          <td style="padding:10px 12px;border-bottom:1px solid #eef2f3;color:#34495e;font-weight:600;width:42%">${k}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #eef2f3;color:#2c3e50">${String(v)}</td>
        </tr>`
      )
      .join('');

    return `
    <!doctype html>
    <html>
      <body style="margin:0;padding:0;background:#f6f9fb;font-family:Poppins,Segoe UI,Arial,sans-serif;color:${textPrimary}">
        <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="background:#f6f9fb;padding:24px 0">
          <tr>
            <td align="center">
              <table role="presentation" width="560" cellpadding="0" cellspacing="0" style="background:${surface};border-radius:12px;box-shadow:0 4px 18px rgba(0,0,0,0.06);overflow:hidden">
                <tr>
                  <td style="background:${brand};color:#ffffff;padding:22px 22px 16px;text-align:center">
                    <div>
                      <img src="${logoUrl}" alt="AM Facilities logo" style="max-width:140px;height:auto;display:inline-block;border:0;outline:none;text-decoration:none" />
                    </div>
                    <div style="font-size:14px;font-weight:600;opacity:0.95;letter-spacing:0.15px;margin-top:8px">Your Quote Summary</div>
                    <div style="height:4px;background:${accent};border-radius:2px;width:140px;margin:14px auto 0 auto"></div>
                  </td>
                </tr>
                <tr>
                  <td style="padding:18px 22px 8px;font-size:14px;line-height:1.6;color:${textPrimary}">
                    Thank you for using AM Facilities! Here are the details of your quote:
                  </td>
                </tr>
                <tr>
                  <td style="padding:0 22px 10px">
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;background:${surface};border-radius:8px;overflow:hidden;border:1px solid ${tableBorder}">
                      ${rowsHtml}
                    </table>
                  </td>
                </tr>
                <tr>
                  <td style="padding:8px 22px 18px">
                    <div style="font-size:22px;color:${accent};font-weight:800;margin:8px 0 4px 0">Estimated Total: $${runningTotal.toFixed(
      2
    )} (before taxes)</div>
                    <div style="font-size:13px;color:${textMuted};margin-top:8px">
                      Ready to proceed? Book online any time here:
                      <a href="https://app.zenmaid.com/book/a-m-facilities" style="color:${accent};text-decoration:underline">ZenMaid Booking</a>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td style="background:#f3f6f8;color:${textMuted};padding:14px 22px;font-size:12px;text-align:center">
                    © ${new Date().getFullYear()} AM Facilities · All rights reserved
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </body>
    </html>`;
  };

  const isValidEmail = (val) => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(val);

  const handleSendEmail = async () => {
    // Prevent double-clicks
    if (sendingRef.current) return;
    
    // Check cooldown
    if (remainingMs > 0) {
      setSendState('error');
      setSendError(`Please wait ${remainingText} before sending another quote.`);
      return;
    }
    
    setSendState('idle');
    setSendError('');
    
    const apiUrl = process.env.NODE_ENV === 'production'
      ? '/api/send-quote-email'
      : 'http://localhost:8787/api/send-quote-email';
    const companyFrom = process.env.REACT_APP_COMPANY_EMAIL || 'no-reply@amfacilities.ca';
    const subject = 'Your AM Facilities cleaning quote';
    const text = buildQuoteSummaryText();
    const html = buildQuoteSummaryHtml();

    try {
      sendingRef.current = true;
      setSendState('sending');
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ to: email, from: companyFrom, subject, text, html }),
      });
      if (!res.ok) {
        const msg = await res.text();
        // If server enforces a wait, sync the local cooldown
        if (res.status === 429) {
          const m = msg && msg.match(/wait\s+(\d+)\s*seconds/i);
          const sec = m ? parseInt(m[1], 10) : null;
          if (sec && sec > 0) {
            const until = Date.now() + sec * 1000;
            setCooldownUntil(until);
            try { localStorage.setItem('amf_quote_email_cooldown', String(until)); } catch {}
          }
        }
        throw new Error(msg || 'Failed to send email');
      }
      setSendState('success');
      // Start 5-minute cooldown on success
      const until = Date.now() + cooldownMs;
      setCooldownUntil(until);
      try { localStorage.setItem('amf_quote_email_cooldown', String(until)); } catch {}
    } catch (e) {
      setSendState('error');
      setSendError(e.message || 'Failed to send email');
    } finally {
      if (sendState !== 'success') sendingRef.current = false;
    }
  };



  return (
    <div className='quote-calculator'>
      <div className='quote-header'>
        {step === 0 && (
          <input
            type='text'
            className='quote-input-inline'
            placeholder='Enter Postal Code'
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setError('');
            }}
          />
        )}
        {step === 1 && (
          <div className='quote-selection-inline'>
            {Object.keys(ROOM_PRICES).map((room) => {
              const displayText = {
                office: 'Office',
                studio: 'Studio',
                one: '1',
                two: '2',
                three: '3',
                four: '4',
                five: '5',
                six: '6',
              };
              return (
                <button
                  key={room}
                  className={`quote-inline-btn ${selectedRoom === room ? 'selected' : ''}`}
                  onClick={() => setSelectedRoom(room)}
                >
                  {displayText[room]}
                </button>
              );
            })}
          </div>
        )}

        {step === 2 && (
          <div className='quote-selection-inline'>
            {Object.keys(BATHROOM_PRICES).map((bathroom) => {
              const displayText = {
                one: '1',
                two: '2',
                three: '3',
                four: '4',
                five: '5',
                six: '6',
              };
              return (
                <button
                  key={bathroom}
                  className={`quote-inline-btn ${selectedBathroom === bathroom ? 'selected' : ''}`}
                  onClick={() => setSelectedBathroom(bathroom)}
                >
                  {displayText[bathroom]}
                </button>
              );
            })}
          </div>
        )}
        {step === 3 && (
          <input
            type='text'
            className='quote-input-inline'
            placeholder='Enter Square Feet'
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setError('');
            }}
          />
        )}
        {step === 4 && (
          <div className='quote-selection-inline'>
            {Object.keys(CLEANING_MULTIPLIERS).map((type) => (
              <button
                key={type}
                className={`quote-inline-btn ${selectedCleaning === type ? 'selected' : ''}`}
                onClick={() => setSelectedCleaning(type)}
                onMouseEnter={() => setTooltip(TOOLTIPS[type])}
                onMouseLeave={() => setTooltip('')}
              >
                {type === 'moveInOut' ? 'Move In/Out' : type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        )}
        {step === 5 && (
          <div className='quote-selection-inline'>
            {Object.keys(EXTRA_PRICES).map((extra) => (
              <button
                key={extra}
                className={`quote-inline-btn ${selectedExtras.includes(extra) ? 'selected' : ''}`}
                onClick={() => toggleExtra(extra)}
              >
                {extra.charAt(0).toUpperCase() + extra.slice(1)}
              </button>
            ))}
          </div>
        )}
        {step === 6 && <input type='text' className='quote-input-inline' value={inputValue} disabled />}
        <button className='quote-next-btn' onClick={handleNext}>
          {step === 6 ? 'Book' : 'Next'}
        </button>
      </div>

      <div className='quote-steps'>
        {['Location', 'Bedrooms', 'Bathrooms', 'Feet', 'Type', 'Extra'].map((label, idx) => (
          <button key={label} className={`quote-step-btn ${step >= idx ? 'active' : ''}`}>
            {label}
          </button>
        ))}
      </div>

      {error && <div className='quote-error'>{error}</div>}
      {tooltip && <div className='quote-tooltip'>{tooltip}</div>}

      <div className='quote-content'>
        <div className='quote-total-section'>
          <h3>Current Quote</h3>
          <div className='quote-total'>
            <span className='quote-rolling' key={runningTotal.toFixed(2)}>
              ${runningTotal.toFixed(2)}
            </span>
          </div>
          {step === 6 && (
            <div className='quote-email'>
              {/* Email functionality is temporarily disabled until API is working
              <div className='quote-email-row'>
                <input
                  type='email'
                  placeholder='Enter your email'
                  className='quote-email-input'
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setSendState('idle');
                    setSendError('');
                  }}
                />
                <button 
                  className='quote-email-btn' 
                  disabled={sendState === 'sending' || remainingMs > 0} 
                  onClick={handleSendEmail}
                  title={remainingMs > 0 ? `Please wait ${remainingText}` : undefined}
                >
                  {sendState === 'sending' ? 'Sending…' : remainingMs > 0 ? `Try again in ${remainingText}` : 'Email quote'}
                </button>
              </div>
              {sendState === 'success' && (
                <div className='quote-email-success'>Quote sent! Please check your inbox.</div>
              )}
              {sendState === 'error' && (
                <div className='quote-email-error'>{sendError || 'Unable to send email at the moment.'}</div>
              )}
              */}
              <div style={{ color: '#607d8b', fontSize: '14px', fontStyle: 'italic', textAlign: 'center' }}>
                Note: Email quote feature is temporarily disabled.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
